import React, { useEffect, useState } from 'react'
import { Box, Button, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionContext } from 'session'
import { formatErrorMessages } from 'interfaces'
import {
  AlertDialog,
  buildContactFromParent,
  CenteredCircularProgress,
  emptyExecOrgAddress,
  execOrgAddressHasContent,
  newExecOrgPhone,
  PHONE_TYPE_ID,
  SerializableMap,
  UsAddressFields,
  usAddressIsValid,
  useExecOrgParentLuts,
  useGlobalMessageHandler,
  useUsRoleTypeLuts
} from 'components'
import {
  type ExecOrgParentSelection,
  ExecutiveOrgParentPicker,
  effectiveParentOrg
} from 'components/us/executives/executive-org-parent-picker'
import {
  type ExecOrgAddress,
  type ExecOrgParentLutItem,
  createExecutiveOrganization,
  execOrgAddressToApi,
  getExecOrgAddress,
  getExecutiveOrganization
} from 'services'
import { FEDERAL_EXECUTIVE_ROLES, STATE_EXECUTIVE_ROLES, isStateExecutiveRole } from 'utils'

const buildEmptyContact = (): ExecOrgAddress => ({
  ...emptyExecOrgAddress(0),
  phones: [newExecOrgPhone(0, 0, true), newExecOrgPhone(0, 0, false)]
})

const EMPTY_SELECTION: ExecOrgParentSelection = {
  roleId: null,
  state: null,
  parentOffice: null,
  childOffice: null
}

const FEDERAL_BRANCH_ROLE_IDS: number[] = [...FEDERAL_EXECUTIVE_ROLES]
const STATE_BRANCH_ROLE_IDS: number[] = [...STATE_EXECUTIVE_ROLES]

export const ExecutiveOrgCreatePage: React.FC = () => {
  const { parentOrgId } = useParams<{ parentOrgId: string }>()
  const parsedParentId = parentOrgId != null ? parseInt(parentOrgId, 10) : 0
  const navigate = useNavigate()
  const { changeModuleName } = useSessionContext()
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const { lutValues: roleLut } = useUsRoleTypeLuts()
  const { lutValues: parentLut, setLutValues: setParentLut } = useExecOrgParentLuts()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [selection, setSelection] = useState<ExecOrgParentSelection>(EMPTY_SELECTION)
  const [roleId, setRoleId] = useState<number | null>(null)
  const [topLevel, setTopLevel] = useState(false)
  const [includeContact, setIncludeContact] = useState(false)
  const [contact, setContact] = useState<ExecOrgAddress>(buildEmptyContact)
  const [parentAddress, setParentAddress] = useState<ExecOrgAddress | null>(null)
  const [warnOpen, setWarnOpen] = useState(false)
  const [warnMissing, setWarnMissing] = useState<string[]>([])

  useEffect(() => { changeModuleName('Create Executive Organization') }, [changeModuleName])

  useEffect(() => {
    if (Number.isNaN(parsedParentId) || parsedParentId === 0) {
      setLoading(false)
      return
    }
    setLoading(true)
    getExecutiveOrganization(parsedParentId)
      .then(parent => {
        const stateRole = isStateExecutiveRole(parent.role.id)
        const parentIsTopLevel = parent.parentOrg == null
        const topOffice: ExecOrgParentLutItem = parentIsTopLevel
          ? parent
          : {
              id: parent.topOrg.id,
              name: parent.topOrg.name,
              code: parent.topOrg.code,
              role: parent.role,
              state: parent.state
            }
        setSelection({
          roleId: parent.role.id,
          state: stateRole ? { id: parent.state.id } : { id: 0 },
          parentOffice: topOffice,
          childOffice: parentIsTopLevel ? null : parent
        })
      })
      .catch(() => { showErrorMessage('Failed to load parent organization.') })
      .finally(() => { setLoading(false) })
  }, [parsedParentId, showErrorMessage])

  useEffect(() => {
    const parentId = effectiveParentOrg(selection)?.id ?? null
    if (parentId == null) {
      setParentAddress(null)
      return
    }
    getExecOrgAddress(parentId)
      .then(setParentAddress)
      .catch(() => { setParentAddress(null) })
  }, [selection])

  const parentOrg = effectiveParentOrg(selection)
  const isStateRole = isStateExecutiveRole(selection.roleId)
  const isTopLevel = topLevel && isStateRole
  const parentRoleId = parentOrg?.role.id ?? selection.roleId

  useEffect(() => { setRoleId(parentRoleId) }, [parentRoleId])

  const branchRoleIds: number[] = parentRoleId == null
    ? []
    : (isStateExecutiveRole(parentRoleId) ? STATE_BRANCH_ROLE_IDS : FEDERAL_BRANCH_ROLE_IDS)

  const roleOptions = branchRoleIds
    .flatMap(id => {
      const role = roleLut.roles.get(id)
      return role == null ? [] : [role]
    })
    .sort((a, b) => a.desc.localeCompare(b.desc))

  const canSave =
    name.trim() !== '' &&
    selection.roleId != null &&
    roleId != null &&
    (isTopLevel || parentOrg != null) &&
    (!isStateRole || selection.state != null) &&
    // Contact is optional, but if included it must carry the fields the backend
    // address serializer requires non-blank (primaryCity + primaryZipcode), so
    // Save can't be enabled into a guaranteed 400/rollback.
    (!includeContact ||
      ((contact.primary.city ?? '').trim() !== '' &&
        (contact.primary.zipCode ?? '').trim() !== ''))

  // Contact is optional; warn (non-blocking) if address or phone is missing so
  // the manager doesn't leave them blank by accident (TI-4100). Website isn't
  // collected on this page — it's added/warned on the detail page's URL editor.
  const missingContactLabels = (): string[] => {
    const hasAddress =
      includeContact &&
      (contact.primary.city ?? '').trim() !== '' &&
      (contact.primary.zipCode ?? '').trim() !== ''
    const hasPhone =
      includeContact &&
      contact.phones.some(
        phone => phone.typeId === PHONE_TYPE_ID &&
          phone.areaCode.trim() !== '' &&
          (phone.phoneNumber ?? '').trim() !== ''
      )
    return [...(hasAddress ? [] : ['address']), ...(hasPhone ? [] : ['phone'])]
  }

  const handleSave = (): void => {
    if (!canSave || roleId == null) return
    if (includeContact) {
      if (!usAddressIsValid(contact, showErrorMessage)) return
    }
    const missing = missingContactLabels()
    if (missing.length > 0) {
      setWarnMissing(missing)
      setWarnOpen(true)
      return
    }
    performCreate()
  }

  const performCreate = (): void => {
    if (!canSave || roleId == null) return
    setSaving(true)
    // The backend phone serializer requires both areaCode and phoneNumber
    // non-blank, so only send fully-filled phones; a partially-entered phone is
    // treated as "not provided" and dropped rather than triggering a 400.
    const phones = contact.phones.filter(
      phone => phone.areaCode.trim() !== '' && (phone.phoneNumber ?? '').trim() !== ''
    )
    const address = includeContact
      ? execOrgAddressToApi({ ...contact, phones }, 0)
      : undefined
    createExecutiveOrganization({
      name: name.trim(),
      code: code.trim() === '' ? null : code.trim(),
      role: { id: roleId },
      state: selection.state != null ? { id: selection.state.id } : null,
      parentOrg: isTopLevel || parentOrg == null ? null : { id: parentOrg.id },
      ...(address != null ? { address } : {})
    })
      .then(response => {
        if (response.succeeded && response.result != null) {
          if (isTopLevel) {
            const created = response.result
            const parents = new SerializableMap(Array.from(parentLut.parents.entries()))
            parents.set(created.id, created)
            setParentLut({ ...parentLut, parents })
          }
          showSuccessMessage('Executive organization created.')
          navigate(`/us/executives/orgs/${response.result.id}`)
        } else {
          const message = formatErrorMessages(response.errors)
          showErrorMessage(
            message !== '' ? message : (response.message ?? 'Failed to create organization.')
          )
        }
      })
      .catch(() => { showErrorMessage('Failed to create organization.') })
      .finally(() => { setSaving(false) })
  }

  const parentHasContent = parentAddress != null && execOrgAddressHasContent(parentAddress)

  const handleUseDefault = (): void => {
    if (parentAddress == null) return
    setContact(buildContactFromParent(contact, parentAddress, 0))
    setIncludeContact(true)
    showSuccessMessage(`Address populated from ${parentOrg?.name ?? 'parent organization'}.`)
  }

  if (Number.isNaN(parsedParentId)) {
    return <Box>Invalid parent organization ID</Box>
  }
  if (loading) {
    return <CenteredCircularProgress loading={loading} size={100} position='absolute' />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box width='97%' sx={{ pb: 1 }}>
        <Typography variant='h4' sx={{ mb: 1 }}>Create Executive Organization</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Parent Role and State only filter the parent list; Role is the new organization&apos;s
          own role and defaults to its parent&apos;s.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label='Organization Name'
              value={name}
              onChange={(event) => { setName(event.target.value) }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label='Code'
              value={code}
              onChange={(event) => { setCode(event.target.value) }}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <ExecutiveOrgParentPicker
            value={selection}
            onChange={setSelection}
            disabled={saving}
            roleLabel='Parent Role *'
            parentOfficeDisabled={isTopLevel}
          />
          {isStateRole && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={topLevel}
                    disabled={saving}
                    onChange={(event) => {
                      setTopLevel(event.target.checked)
                      if (event.target.checked) {
                        setSelection({ ...selection, parentOffice: null, childOffice: null })
                      }
                    }}
                  />
                }
                label='Top-level organization'
              />
              {isTopLevel && (
                <Typography variant='body2' color='text.secondary'>
                  Creates a top-level organization with no parent in the selected state.
                </Typography>
              )}
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Role *</InputLabel>
              <Select
                label='Role *'
                disabled={saving || parentRoleId == null}
                value={roleId ?? ''}
                onChange={(event) => { setRoleId(Number(event.target.value)) }}
              >
                {roleOptions.length === 0 && parentRoleId != null
                  ? <MenuItem value={parentRoleId}>Parent&apos;s role</MenuItem>
                  : roleOptions.map(role => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.id === parentRoleId && !isTopLevel ? `${role.desc} (parent's)` : role.desc}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={<Switch checked={includeContact} onChange={(event) => { setIncludeContact(event.target.checked) }} disabled={saving} />}
                label='Add default address & phone'
              />
              {parentHasContent && (
                <Tooltip title={`Copy address & phone from ${parentOrg?.name ?? 'the parent organization'}`}>
                  <span>
                    <Button variant='outlined' size='small' disabled={saving} onClick={handleUseDefault}>
                      Use Default
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Grid>
          {includeContact && (
            <Grid item xs={12}>
              <UsAddressFields
                comAddress={contact}
                updateAddress={(updated) => { setContact(updated as ExecOrgAddress) }}
                resetAddress={() => { setContact(buildEmptyContact()) }}
                disabled={saving}
                showPhones
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button
                variant='outlined'
                disabled={saving}
                onClick={() => { navigate(parsedParentId === 0 ? '/us/executives/orgs' : `/us/executives/orgs/${parsedParentId}`) }}
              >
                Cancel
              </Button>
              <Button variant='contained' disabled={!canSave || saving} onClick={handleSave}>
                Save
              </Button>
            </Stack>
          </Grid>
        </Grid>
        <AlertDialog
          open={warnOpen}
          title='Missing contact information'
          contentText={`This organization is missing: ${warnMissing.join(', ')}. You can go back and add it, or save without it.`}
          nameButtonCancel='Go Back'
          nameButtonConfirm='Save Anyway'
          onButtonClick={() => { setWarnOpen(false); performCreate() }}
          onClose={() => { setWarnOpen(false) }}
          containedButton
        />
      </Box>
    </Box>
  )
}
